import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import './grid.css';

const noop = () => {};

const assignRef = (ref, value) => {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  ref.current = value;
};

const useCombinedRefs = (...refs) => {
  return value => {
    refs.forEach(ref => assignRef(ref, value));
  };
};

const getTransitionDuration = timeout => {
  if (typeof timeout === 'number') return timeout;
  if (typeof timeout === 'object' && timeout !== null) {
    return timeout.enter || timeout.exit || 200;
  }
  return 200;
};

const getPlacementStyles = (anchorRect, nodeRect, placement = 'bottom', gap = 8) => {
  const [base, align = 'center'] = placement.split('-');
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = 0;
  let left = 0;
  let finalBase = base;

  if (base === 'bottom') {
    const spaceBelow = viewportHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    if (spaceBelow < nodeRect.height + gap && spaceAbove > spaceBelow) {
      finalBase = 'top';
    }
  }

  if (base === 'top') {
    const spaceAbove = anchorRect.top;
    const spaceBelow = viewportHeight - anchorRect.bottom;
    if (spaceAbove < nodeRect.height + gap && spaceBelow > spaceAbove) {
      finalBase = 'bottom';
    }
  }

  if (finalBase === 'bottom' || finalBase === 'top') {
    top = finalBase === 'bottom' ? anchorRect.bottom + gap : anchorRect.top - nodeRect.height - gap;
    if (align === 'start') {
      left = anchorRect.left;
    } else if (align === 'end') {
      left = anchorRect.right - nodeRect.width;
    } else {
      left = anchorRect.left + anchorRect.width / 2 - nodeRect.width / 2;
    }
  } else {
    left = finalBase === 'left' ? anchorRect.left - nodeRect.width - gap : anchorRect.right + gap;
    if (align === 'start') {
      top = anchorRect.top;
    } else if (align === 'end') {
      top = anchorRect.bottom - nodeRect.height;
    } else {
      top = anchorRect.top + anchorRect.height / 2 - nodeRect.height / 2;
    }
  }

  const maxLeft = Math.max(8, viewportWidth - nodeRect.width - 8);
  const maxTop = Math.max(8, viewportHeight - nodeRect.height - 8);
  const safeLeft = Math.min(maxLeft, Math.max(8, left));
  const safeTop = Math.min(maxTop, Math.max(8, top));

  return {
    top: `${safeTop + window.scrollY}px`,
    left: `${safeLeft + window.scrollX}px`,
  };
};

const normalizeGridSize = value => {
  if (value === true) return 'true';
  if (value === 'auto') return 'auto';

  if (typeof value === 'number' && Number.isFinite(value)) {
    const normalized = Math.min(12, Math.max(1, Math.round(value)));
    return String(normalized);
  }

  return undefined;
};

const Paper = forwardRef(({ className, style, elevation = 1, component: Component = 'div', ...props }, ref) => (
  <Component
    ref={ref}
    className={cn('rounded-md bg-white', elevation > 0 && 'shadow-sm', className)}
    style={style}
    {...props}
  />
));
Paper.displayName = 'Paper';

const AppBar = forwardRef(
  ({ className, style, position = 'fixed', color = 'primary', component: Component = 'header', ...props }, ref) => {
    const positionClass =
      position === 'fixed'
        ? 'fixed inset-x-0 top-0 z-40'
        : position === 'sticky'
          ? 'sticky top-0 z-40'
          : 'relative';

    return (
      <Component
        ref={ref}
        className={cn('w-full', positionClass, color === 'default' ? 'bg-white' : 'bg-slate-900 text-white', className)}
        style={style}
        {...props}
      />
    );
  }
);
AppBar.displayName = 'AppBar';

const Grid = forwardRef((props, ref) => {
  const {
    alignItems,
    children,
    className,
    classes,
    component: Component = 'div',
    container = false,
    direction = 'row',
    item,
    justify,
    justifyContent,
    lg,
    md,
    sm,
    spacing,
    style,
    wrap = 'wrap',
    xl,
    xs,
    ...rest
  } = props;

  const isItem = item || xs != null || sm != null || md != null || lg != null || xl != null;

  const inlineStyle = {
    ...style,
  };

  if (container && spacing != null) {
    const gap = Number(spacing);
    inlineStyle.gap = Number.isFinite(gap) ? `${gap}px` : spacing;
  }

  const justification = justifyContent ?? justify;
  if (container && justification) {
    inlineStyle.justifyContent = justification;
  }

  if (container && alignItems) {
    inlineStyle.alignItems = alignItems;
  }

  return (
    <Component
      ref={ref}
      className={cn(
        'ui-grid',
        container && 'ui-grid-container',
        isItem && 'ui-grid-item',
        direction === 'column' && 'flex-col',
        direction === 'column-reverse' && 'flex-col-reverse',
        direction === 'row-reverse' && 'flex-row-reverse',
        wrap === 'nowrap' && 'flex-nowrap',
        wrap === 'wrap-reverse' && 'flex-wrap-reverse',
        classes?.root,
        container && classes?.container,
        isItem && classes?.item,
        className
      )}
      style={inlineStyle}
      data-xs={normalizeGridSize(xs)}
      data-sm={normalizeGridSize(sm)}
      data-md={normalizeGridSize(md)}
      data-lg={normalizeGridSize(lg)}
      data-xl={normalizeGridSize(xl)}
      {...rest}
    >
      {children}
    </Component>
  );
});
Grid.displayName = 'Grid';

const Checkbox = forwardRef(
  (
    {
      checked,
      className,
      color = 'default',
      defaultChecked,
      disabled,
      id,
      onChange,
      style,
      ...props
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = useState(defaultChecked || false);
    const isChecked = checked !== undefined ? checked : internalChecked;

    const handleChange = e => {
      if (checked === undefined) {
        setInternalChecked(e.target.checked);
      }
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <span
        className={cn(
          'relative inline-flex items-center justify-center p-[12px] w-[48px] h-[48px] rounded-full cursor-pointer select-none transition-colors hover:bg-black/5',
          disabled && 'pointer-events-none opacity-30',
          className
        )}
        style={style}
      >
        <input
          id={id}
          ref={ref}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          {...props}
        />
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className={cn(
            'w-[24px] h-[24px] fill-current transition-colors',
            isChecked
              ? color === 'primary'
                ? 'text-[#5882C1]'
                : 'text-slate-700'
              : 'text-black/54'
          )}
        >
          {isChecked ? (
            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          ) : (
            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
          )}
        </svg>
      </span>
    );
  }
);
Checkbox.displayName = 'Checkbox';

const CircularProgress = ({ className, classes, color = 'primary', size = 40, style, ...props }) => {
  const colorClass = color === 'inherit' ? 'text-current' : color === 'secondary' ? 'text-violet-600' : 'text-[#005f98]';

  return (
    <span
      role="progressbar"
      className={cn(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        colorClass,
        classes?.root,
        className
      )}
      style={{ width: size, height: size, ...style }}
      {...props}
    />
  );
};

const LinearProgress = ({ className, style, ...props }) => (
  <div className={cn('h-1.5 w-full overflow-hidden rounded bg-slate-200', className)} style={style} {...props}>
    <div className="h-full w-full animate-pulse bg-[#005f98]" />
  </div>
);

const ClickAwayListener = ({ children, onClickAway = noop }) => {
  const nodeRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = event => {
      const node = nodeRef.current;
      if (!node) return;

      if (!node.contains(event.target)) {
        onClickAway(event);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [onClickAway]);

  return (
    <div ref={nodeRef}>
      {children}
    </div>
  );
};

const Collapse = ({ children, className, in: inProp = false, style, timeout = 200, unmountOnExit = false, ...props }) => {
  const duration = getTransitionDuration(timeout);
  const [mounted, setMounted] = useState(inProp);

  useEffect(() => {
    if (inProp) {
      setMounted(true);
      return;
    }

    if (unmountOnExit) {
      const timer = window.setTimeout(() => {
        setMounted(false);
      }, duration);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [duration, inProp, unmountOnExit]);

  if (!mounted && unmountOnExit) {
    return null;
  }

  return (
    <div
      className={cn('overflow-hidden transition-all', className)}
      style={{
        maxHeight: inProp ? '3000px' : '0px',
        opacity: inProp ? 1 : 0,
        transitionDuration: `${duration}ms`,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

const Grow = ({
  children,
  className,
  in: inProp = false,
  onEnter,
  onEntered,
  onExit,
  onExited,
  timeout = 200,
  unmountOnExit = false,
}) => {
  const duration = getTransitionDuration(timeout);
  const [mounted, setMounted] = useState(inProp);

  useEffect(() => {
    if (inProp) {
      setMounted(true);
      onEnter?.();

      const enteredTimer = window.setTimeout(() => {
        onEntered?.();
      }, duration);

      return () => window.clearTimeout(enteredTimer);
    }

    onExit?.();

    const exitedTimer = window.setTimeout(() => {
      onExited?.();
    }, duration);

    if (unmountOnExit) {
      const timer = window.setTimeout(() => {
        setMounted(false);
      }, duration);

      return () => {
        window.clearTimeout(timer);
        window.clearTimeout(exitedTimer);
      };
    }

    return () => {
      window.clearTimeout(exitedTimer);
    };
  }, [duration, inProp, onEnter, onEntered, onExit, onExited, unmountOnExit]);

  if (!mounted && unmountOnExit) return null;

  if (!isValidElement(children)) return children;

  return cloneElement(children, {
    className: cn(children.props.className, className),
    style: {
      transform: inProp ? 'scale(1)' : 'scale(0.97)',
      opacity: inProp ? 1 : 0,
      transformOrigin: 'top center',
      transition: `transform ${duration}ms ease, opacity ${duration}ms ease`,
      ...children.props.style,
    },
  });
};

const Fade = ({
  children,
  className,
  in: inProp = false,
  onEnter,
  onEntered,
  onExit,
  onExited,
  timeout = 200,
  unmountOnExit = false,
}) => {
  const duration = getTransitionDuration(timeout);
  const [mounted, setMounted] = useState(inProp);

  useEffect(() => {
    if (inProp) {
      setMounted(true);
      onEnter?.();

      const enteredTimer = window.setTimeout(() => {
        onEntered?.();
      }, duration);

      return () => window.clearTimeout(enteredTimer);
    }

    onExit?.();

    const exitedTimer = window.setTimeout(() => {
      onExited?.();
    }, duration);

    if (unmountOnExit) {
      const timer = window.setTimeout(() => {
        setMounted(false);
      }, duration);

      return () => {
        window.clearTimeout(timer);
        window.clearTimeout(exitedTimer);
      };
    }

    return () => {
      window.clearTimeout(exitedTimer);
    };
  }, [duration, inProp, onEnter, onEntered, onExit, onExited, unmountOnExit]);

  if (!mounted && unmountOnExit) return null;
  if (!isValidElement(children)) return children;

  return cloneElement(children, {
    className: cn(children.props.className, className),
    style: {
      opacity: inProp ? 1 : 0,
      transition: `opacity ${duration}ms ease`,
      ...children.props.style,
    },
  });
};

const PortalLayer = ({ children }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
};

const useEscapeToClose = (open, onClose) => {
  useEffect(() => {
    if (!open || typeof onClose !== 'function') return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);
};

const Modal = ({ children, className, onClose, open = false, style, ...props }) => {
  useEscapeToClose(open, onClose);

  if (!open) return null;

  const handleBackdropClick = event => {
    if (event.target === event.currentTarget && typeof onClose === 'function') {
      onClose(event);
    }
  };

  return (
    <PortalLayer>
      <div
        className={cn('fixed inset-0 z-[1300] bg-black/50', className)}
        style={style}
        onMouseDown={handleBackdropClick}
        role="presentation"
        {...props}
      >
        {children}
      </div>
    </PortalLayer>
  );
};

const DIALOG_WIDTH_CLASS = {
  xs: 'max-w-xs',
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

const Dialog = ({ children, className, maxWidth = 'sm', onClose, open = false, PaperProps = {}, ...props }) => {
  useEscapeToClose(open, onClose);

  if (!open) return null;

  const handleBackdropClick = event => {
    if (event.target === event.currentTarget && typeof onClose === 'function') {
      onClose(event);
    }
  };

  return (
    <PortalLayer>
      <div
        className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 p-4"
        onMouseDown={handleBackdropClick}
        role="presentation"
      >
        <div
          className={cn(
            'relative w-full max-h-[90vh] overflow-auto rounded-lg bg-white shadow-2xl',
            DIALOG_WIDTH_CLASS[maxWidth] || DIALOG_WIDTH_CLASS.sm,
            PaperProps.className,
            className
          )}
          style={PaperProps.style}
          onMouseDown={event => event.stopPropagation()}
          {...props}
        >
          {children}
        </div>
      </div>
    </PortalLayer>
  );
};

const DialogTitle = ({ children, className, component: Component = 'h2', ...props }) => (
  <Component className={cn('px-6 pt-5 text-lg font-semibold text-slate-900', className)} {...props}>
    {children}
  </Component>
);

const DialogContent = ({ children, className, ...props }) => (
  <div className={cn('px-6 pb-4 pt-2 text-sm text-slate-700', className)} {...props}>
    {children}
  </div>
);

const DialogContentText = ({ children, className, component: Component = 'p', ...props }) => (
  <Component className={cn('text-sm leading-relaxed text-slate-700', className)} {...props}>
    {children}
  </Component>
);

const DialogActions = ({ children, className, ...props }) => (
  <div className={cn('flex items-center justify-end gap-2 px-6 pb-5 pt-2', className)} {...props}>
    {children}
  </div>
);

const Popper = ({ anchorEl, children, className, open = false, placement = 'bottom', style, transition = false, disablePortal = false }) => {
  const nodeRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState({ top: '0px', left: '0px' });
  const [isPositioned, setIsPositioned] = useState(false);

  const resolvedAnchor = typeof anchorEl === 'function' ? anchorEl() : anchorEl;

  useLayoutEffect(() => {
    if (!open || !resolvedAnchor) {
      setIsPositioned(false);
      return undefined;
    }

    setIsPositioned(false);

    const updatePosition = () => {
      const anchorRect = resolvedAnchor.getBoundingClientRect();
      const nodeRect = nodeRef.current?.getBoundingClientRect() || { width: 0, height: 0 };
      setPositionStyle(getPlacementStyles(anchorRect, nodeRect, placement));
      setIsPositioned(true);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, placement, resolvedAnchor]);

  if (!open || !resolvedAnchor) return null;

  const renderedChildren = transition && typeof children === 'function' ? children({ TransitionProps: { in: open } }) : children;

  const rootClass = disablePortal ? 'absolute z-[1600] w-max' : 'absolute z-[1600] w-max';

  if (disablePortal) {
    return (
      <div
        ref={nodeRef}
        className={cn(rootClass, className)}
        style={{ ...positionStyle, visibility: isPositioned ? 'visible' : 'hidden', ...style }}
        role="presentation"
      >
        {renderedChildren}
      </div>
    );
  }

  return (
    <PortalLayer>
      <div
        ref={nodeRef}
        className={cn(rootClass, className)}
        style={{ ...positionStyle, visibility: isPositioned ? 'visible' : 'hidden', ...style }}
        role="presentation"
      >
        {renderedChildren}
      </div>
    </PortalLayer>
  );
};

const Tooltip = ({ children, classes, placement = 'bottom', title }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState({ top: '0px', left: '0px' });
  const [isPositioned, setIsPositioned] = useState(false);

  const hasTitle = title !== null && title !== undefined && title !== '';

  useLayoutEffect(() => {
    if (!open || !hasTitle || !triggerRef.current) {
      setIsPositioned(false);
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const anchorRect = triggerRef.current.getBoundingClientRect();
      const nodeRect = tooltipRef.current?.getBoundingClientRect() || { width: 0, height: 0 };
      setPositionStyle(getPlacementStyles(anchorRect, nodeRect, placement));
      setIsPositioned(true);
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [hasTitle, open, placement]);

  if (!hasTitle) {
    return children;
  }

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open && (
        <PortalLayer>
          <div
            ref={tooltipRef}
            className={cn(
              'absolute z-[1700] w-max max-w-[500px] rounded bg-[#616161]/90 px-2 py-1.5 text-[11px] font-medium text-white shadow-md',
              classes?.tooltip
            )}
            style={{
              ...positionStyle,
              visibility: isPositioned ? 'visible' : 'hidden',
              opacity: isPositioned ? 1 : 0,
              transition: 'opacity 150ms ease',
            }}
            role="tooltip"
          >
            {title}
          </div>
        </PortalLayer>
      )}
    </>
  );
};

const IconButton = forwardRef(({ children, className, type = 'button', ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn('inline-flex items-center justify-center rounded-full p-2 transition hover:bg-slate-100', className)}
    {...props}
  >
    {children}
  </button>
));
IconButton.displayName = 'IconButton';

const List = ({ children, className, component: Component = 'ul', disablePadding = false, ...props }) => (
  <Component className={cn('list-none', disablePadding ? 'm-0 p-0' : 'm-0 p-2', className)} {...props}>
    {children}
  </Component>
);

const ListItem = ({ children, className, component: Component = 'li', disableGutters = false, ...props }) => (
  <Component className={cn('flex items-start gap-2', disableGutters ? 'px-0 py-1' : 'px-2 py-1', className)} {...props}>
    {children}
  </Component>
);

const ListItemIcon = ({ children, className, classes, ...props }) => (
  <span className={cn('inline-flex min-w-6 items-center justify-center', classes?.root, className)} {...props}>
    {children}
  </span>
);

const ListItemText = ({ children, className, ...props }) => (
  <span className={cn('text-sm text-slate-700', className)} {...props}>
    {children}
  </span>
);

const MenuItem = forwardRef(
  (
    {
      buttonRef,
      children,
      className,
      component: Component = 'li',
      disabled = false,
      selected = false,
      style,
      ...props
    },
    ref
  ) => {
    const combinedRef = useCombinedRefs(ref, buttonRef);

    return (
      <Component
        ref={combinedRef}
        className={cn(
          'cursor-pointer px-3 py-2 text-sm text-slate-800',
          selected && 'bg-slate-100',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
        style={style}
        {...props}
      >
        {children}
      </Component>
    );
  }
);
MenuItem.displayName = 'MenuItem';

const StepperContext = createContext({
  activeStep: 0,
  alternativeLabel: false,
  connector: null,
});

const StepContext = createContext({
  active: false,
  completed: false,
  index: 0,
});

const Stepper = ({ activeStep = 0, alternativeLabel = false, children, className, connector, ...props }) => {
  const steps = Children.toArray(children).filter(Boolean);

  return (
    <StepperContext.Provider value={{ activeStep, alternativeLabel, connector }}>
      <div className={cn('w-full', className)} {...props}>
        <div className={cn('flex w-full items-start')}>
          {steps.map((step, index) => {
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;

            return (
              <React.Fragment key={step?.key ?? index}>
                <StepContext.Provider value={{ active: isActive, completed: isCompleted, index }}>
                  <div className="flex min-w-0 flex-1 justify-center">{step}</div>
                </StepContext.Provider>
                {index < steps.length - 1 &&
                  (connector ? (
                    cloneElement(connector, {
                      active: isActive,
                      completed: isCompleted,
                    })
                  ) : (
                    <StepConnector active={isActive} completed={isCompleted} />
                  ))}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </StepperContext.Provider>
  );
};

const Step = ({ children, className, active, completed, index, ...props }) => (
  <div className={cn('flex flex-col items-center text-center', className)} {...props}>
    {children}
  </div>
);

const StepLabel = ({ children, className, icon, active: activeProp, completed: completedProp, ...props }) => {
  const { active, completed } = useContext(StepContext);

  return (
    <div className={cn('flex flex-col items-center gap-2 text-center text-xs', className)} {...props}>
      {icon && <span className="inline-flex items-center justify-center">{icon}</span>}
      <span className={cn(active ? 'font-semibold text-slate-900' : completed ? 'text-slate-700' : 'text-slate-500')}>
        {children}
      </span>
    </div>
  );
};

const StepConnector = ({ className, active, completed, ...props }) => (
  <div className={cn('mx-2 mt-5 h-px min-w-6 flex-1 bg-slate-300', className)} {...props} />
);

const Table = ({ children, className, ...props }) => (
  <div className="w-full overflow-x-auto">
    <table className={cn('w-full border-collapse text-left text-sm', className)} {...props}>
      {children}
    </table>
  </div>
);

const TableHead = ({ children, className, ...props }) => (
  <thead className={cn('border-b border-slate-200 bg-slate-50', className)} {...props}>
    {children}
  </thead>
);

const TableBody = ({ children, className, ...props }) => (
  <tbody className={cn('divide-y divide-slate-100', className)} {...props}>
    {children}
  </tbody>
);

const TableRow = ({ children, className, ...props }) => (
  <tr className={cn('border-b border-slate-100 last:border-b-0', className)} {...props}>
    {children}
  </tr>
);

const TableCell = ({ children, className, component: Component, scope, ...props }) => {
  const ResolvedComponent = Component || (scope === 'row' ? 'th' : 'td');

  return (
    <ResolvedComponent className={cn('px-3 py-2 align-middle text-sm text-slate-700', className)} scope={scope} {...props}>
      {children}
    </ResolvedComponent>
  );
};

const TableSortLabel = ({ active = false, children, className, direction = 'asc', onClick, ...props }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1 text-left text-sm transition-colors',
      active ? 'text-slate-900' : 'text-inherit hover:text-slate-900',
      className
    )}
    {...props}
  >
    <span>{children}</span>
    <span className={cn('transition-transform duration-200', !active && 'opacity-30', direction === 'desc' ? 'rotate-0' : 'rotate-180')}>
      <ArrowDown size={16} />
    </span>
  </button>
);

const TablePagination = ({
  className,
  component: Component = 'div',
  count = 0,
  labelRowsPerPage = 'Rows per page',
  onChangePage,
  page = 0,
  rowsPerPage = 10,
  style,
}) => {
  const totalPages = Math.max(1, Math.ceil(count / Math.max(1, rowsPerPage)));
  const currentPage = Math.min(page, totalPages - 1);
  const from = count === 0 ? 0 : currentPage * rowsPerPage + 1;
  const to = Math.min(count, (currentPage + 1) * rowsPerPage);

  return (
    <Component
      className={cn('mt-3 flex flex-wrap items-center justify-end gap-3 text-sm text-slate-600', className)}
      style={style}
    >
      {labelRowsPerPage !== null && <span>{labelRowsPerPage}</span>}
      <span>
        {from}-{to} de {count}
      </span>
      <button
        type="button"
        className="rounded border border-slate-300 px-2 py-1 text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={event => onChangePage?.(event, Math.max(0, currentPage - 1))}
        disabled={currentPage <= 0}
      >
        ‹
      </button>
      <button
        type="button"
        className="rounded border border-slate-300 px-2 py-1 text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={event => onChangePage?.(event, Math.min(totalPages - 1, currentPage + 1))}
        disabled={currentPage >= totalPages - 1}
      >
        ›
      </button>
    </Component>
  );
};

const TextField = ({
  className,
  fullWidth = false,
  InputProps,
  multiline = false,
  onChange,
  placeholder,
  rows = 3,
  style,
  type = 'text',
  value,
  ...props
}) => {
  const inputClassName = cn(
    'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-slate-400',
    multiline ? 'resize-y' : '',
    InputProps?.disableUnderline && 'border-none shadow-none focus:border-none',
    InputProps?.classes?.input
  );

  const sharedProps = {
    className: inputClassName,
    onChange,
    placeholder,
    value,
    ...props,
  };

  return (
    <div className={cn(fullWidth && 'w-full', className)} style={style}>
      {multiline ? <textarea rows={rows} {...sharedProps} /> : <input type={type} {...sharedProps} />}
    </div>
  );
};

const Typography = ({ children, className, component, variant = 'body1', ...props }) => {
  const map = {
    h6: { tag: 'h6', className: 'text-lg font-semibold' },
    subtitle1: { tag: 'h6', className: 'text-sm font-semibold' },
    subtitle2: { tag: 'h6', className: 'text-xs font-semibold uppercase tracking-wide' },
    body1: { tag: 'p', className: 'text-sm' },
    body2: { tag: 'p', className: 'text-xs' },
  };

  const resolved = map[variant] || map.body1;
  const Component = component || resolved.tag;

  return (
    <Component className={cn(resolved.className, className)} {...props}>
      {children}
    </Component>
  );
};

const Divider = ({ className, ...props }) => <hr className={cn('my-2 border-slate-200', className)} {...props} />;

export {
  AppBar,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fade,
  Grid,
  Grow,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Modal,
  Paper,
  Popper,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
};

export default {
  AppBar,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fade,
  Grid,
  Grow,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Modal,
  Paper,
  Popper,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
};
